import { logError } from './logger';

const CurrencyFormatter = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

/**
 * Formats a number to a currency string with the option to return back a number with the $ stripped off.
 * @param number The currency amount.
 * @returns  The currency string in the format $12,345.67
 */
export function formatCurrency(number: unknown) {
  return CurrencyFormatter.format(Number(number ?? 0));
}

/**
 * Formats a date using a standard format.
 * @param date : string|Number|date A date.
 * @returns {string} The formatted date string.
 */
export function formatDate(date) {
  return new Date(date).toLocaleDateString('en-US');
}

/**
 * Matches a zero-padded US date string, e.g. "01/05/2025".
 */
const US_DATE_PATTERN = /^\d{2}\/\d{2}\/\d{4}$/;

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Strictly parses a zero-padded US date string ("MM/DD/YYYY") into a local Date.
 *
 * Prefer this over `new Date(value)` when the input is user- or API-supplied:
 * it rejects malformed input and dates that do not exist ("02/31/2025"), and it
 * builds the Date from components so the result is never shifted by the local
 * UTC offset.
 *
 * Years below 1000 are rejected so that a part-typed year coming off a masked
 * date picker ("01/01/0202") is not accepted as a real date.
 *
 * @param value A zero-padded US date string. Unpadded input ("1/5/2025") is rejected.
 * @returns {Date|null} The parsed date, or null when the value is not a real US date.
 */
export function parseUsDate(value: string | null | undefined): Date | null {
  if (!value || !US_DATE_PATTERN.test(value)) {
    return null;
  }

  const [month, day, year] = value.split('/').map(Number);

  if (year < 1000) {
    return null;
  }

  const parsed = new Date(year, month - 1, day);

  const isRealDate =
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day;

  return isRealDate ? parsed : null;
}

/**
 * Parses a date that may arrive either as ISO ("YYYY-MM-DD", optionally with a
 * time component) or as a zero-padded US date ("MM/DD/YYYY").
 *
 * ISO values are split into their parts rather than handed to `new Date()`,
 * which would read a bare "YYYY-MM-DD" as UTC midnight and report the previous
 * day in any negative-offset (US) timezone.
 *
 * Note that `dayjs` cannot stand in here: strict parsing needs the
 * `customParseFormat` plugin, which this app does not load, so
 * `dayjs('02/31/2025', 'MM/DD/YYYY', true)` reports itself as valid.
 *
 * @param value An ISO or zero-padded US date string.
 * @returns {Date|null} The parsed date, or null when the value cannot be parsed.
 */
export function parseLoanDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  const isoMatch = ISO_DATE_PATTERN.exec(value);

  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return parseUsDate(`${month}/${day}/${year}`);
  }

  return parseUsDate(value);
}

/**
 * Converts a date to the UTC format "2024-10-11T04:00:00.000Z".
 * @param date : string|Number|date A date.
 * @param daysOffset: number of days to add or subtract from the date before converting to UTC
 * @returns {number} The formatted date string in UTC.
 */
export function formatDateToUTCWithOffset(date, daysOffset = 0) {
  const dateObj = new Date(date);
  dateObj.setDate(dateObj.getDate() + daysOffset);
  return Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
}

/**
 * Converts a date string to the format "yyyy-MM-dd HH:mm:ss.ffff".
 * @param {string} dateString - The input date string.
 * @returns {string} The formatted date string.
 */
export function convertToCustomFormat(dateString) {
  const date = new Date(dateString);

  const pad = (num, size) => String(num).padStart(size, '0');

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1, 2);
  const day = pad(date.getDate(), 2);
  const hours = pad(date.getHours(), 2);
  const minutes = pad(date.getMinutes(), 2);
  const seconds = pad(date.getSeconds(), 2);
  const milliseconds = pad(date.getMilliseconds(), 4);

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
}

/**
 * get month name from  a date using a standard format.
 * @param date : string|Number|date A date.
 * @returns {string} Month name string.
 */
export function formatDateStringToMonthName(date) {
  return new Date(date).toLocaleString('en-us', { month: 'long' });
}

/**
 * Formats a string (currency consisting of commas and .) into a standard number format.
 * @returns The formatted currency.
 */
export function reverseFormatCurrency(str: string | number) {
  return Number.parseFloat(str?.toString()?.replaceAll(',', ''));
}

/**
 * Formats a LIN (Loan Identification Number) by removing hyphens.
 * Handles both string and number inputs.
 *
 * @param value - The LIN value to format (string or number)
 * @returns The formatted LIN with hyphens removed, or empty string if value is falsy
 */
export const formatLINForComparison = (value: string | number): string => {
  if (!value) return '';
  return String(value).replaceAll('-', '');
};

/**
 * Masks the first five digits of an SSN with # while preserving its standard format.
 */
export const maskSocialSecurity = (value: string | undefined): string => {
  if (!value) return '';
  const digits = value.replaceAll(/\D/g, '');
  if (digits.length === 0) return '';
  const masked = digits
    .split('')
    .map((digit, index) => (index < 5 ? '#' : digit))
    .join('');
  if (masked.length <= 3) return masked;
  if (masked.length <= 5) return `${masked.slice(0, 3)}-${masked.slice(3)}`;
  return `${masked.slice(0, 3)}-${masked.slice(3, 5)}-${masked.slice(5, 9)}`;
};

/**
 * Formats a string (SSN value consists of digit and -) into a standard SSN value format.
 * @param val: string initial unformatted SSN
 * @returns {string} The formatted SSN string.
 */
export const formatSocialSecurity = (val): string => {
  const regex = '^[0-9-]*$';
  const parseNumber = (string) => (string?.match(regex) || []).join('');
  const valueAsNumber = parseNumber(val?.toString());

  let rawSSN = valueAsNumber.replaceAll('-', '');

  if (rawSSN.length >= 9) {
    rawSSN = rawSSN.substring(0, 9);
  }

  let res;

  if (rawSSN.length <= 3) {
    res = rawSSN;
  } else if (rawSSN.length > 3 && rawSSN.length <= 5) {
    res = rawSSN.substring(0, 3) + '-' + rawSSN.substring(3, rawSSN.length);
  } else if (rawSSN.length > 5 && rawSSN.length <= 9) {
    res =
      rawSSN.substring(0, 3) +
      '-' +
      rawSSN.substring(3, 5) +
      '-' +
      rawSSN.substring(5, rawSSN.length);
  }

  return res;
};

/**
 * Formats the input name (string) for the override question.
 * @param str: string The Input Name. (e.g. Variable Hourly Income)
 * @returns {string} The formatted string. (e.g. variable hourly)
 */
export const formatOverrideInput = (str) =>
  str
    .split(' ')
    .filter((str) => !str.includes('Income'))
    .map((str) => str.toLowerCase())
    .join(' ');

const numberAccept = /[\d.]+/g;
const parseNumber = (string) => (string.match(numberAccept) || []).join('');

const formatFloatingPointNumber = (value, maxDigits) => {
  if (value === null) return;
  const valueAsNumber = parseNumber(value.toString());
  const [dollars, cents] = valueAsNumber.split('.');

  // Avoid rounding errors at toLocaleString as when user enters 1.239 and maxDigits=2 we
  // must not to convert it to 1.24, it must stay 1.23
  const scaledTail = cents == null ? '' : cents.slice(0, maxDigits);
  const number = Number.parseFloat(`${dollars}.${scaledTail}`);

  if (Number.isNaN(number)) {
    return '';
  }

  const valueAsCurrency = number.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDigits,
  });

  if (valueAsNumber.includes('.')) {
    const [formattedHead] = valueAsCurrency.split('.');
    return `${formattedHead}.${scaledTail}`;
  }
  return valueAsCurrency;
};

/**
 * Convert a text string to a number. (may not be exhaustive)
 * @param text A string containing a number written in english.
 * @return {number} The requested number (probably).
 */
export const textToNumber = (text) => {
  switch (text.toLowerCase()) {
    case 'one':
      return 1;
    case 'two':
      return 2;
    case 'three':
      return 3;
    default:
      return Number.NaN;
  }
};

/**
 * Formats a string to a currency string.
 * @param string : string The currency amount.
 * @returns {string} The formatted currency string.
 */
export const formatCurrencyValue = (string) =>
  formatFloatingPointNumber(string, 2);

/**
 * formats a string from the datepicker 'mm/dd/yyyy' to DateTime 'yyyy-mm-dd'
 * @param {string} dateString 'mm/dd/yyyy'
 * @returns {string} a string parsed into server DateTime format yyyy-mm-dd
 */
export const formatDateStringToDateTime = (dateString) => {
  const date = new Date(dateString);
  return `${date.getFullYear()}-${appendZero(date.getMonth() + 1)}-${appendZero(
    date.getDate(),
  )}`;
};

const appendZero = (number) => {
  return number < 10 ? `0${number}` : `${number}`;
};

/**
 * Parse the overridden amount type.
 * @param string : e.g. overriddenBaseIncome.
 * @returns {string} e.g. base.
 */
export const parseOverriddenType = (type) =>
  type
    .split(/(?=[A-Z])/)
    .map((w) => w.toLowerCase())
    .filter((w) => !['overridden', 'income'].includes(w))
    .join(' ');

/**
 * Converts a string to a nullable boolean value.
 * @param value {string} The string to convert.
 * @return {null|boolean} The boolean, or null if it is undefined.
 */
export function stringToNullableBoolean(value) {
  switch (value?.toLowerCase()) {
    case 'true':
    case '1':
      return true;
    case 'false':
    case '0':
      return false;
    default:
      return null;
  }
}

/**
 given a date range ( startDays, endDays ) retuns true if  date is in  range 
 */

export function isDateInBetween(date, startDays, endDays) {
  if (!date || !startDays || !endDays) {
    return false;
  }
  const today = new Date();
  const givenDate = new Date(date);
  const differenceInTime = today.getTime() - givenDate.getTime();
  const differenceInDays = differenceInTime / (1000 * 3600 * 24);
  return differenceInDays > startDays && differenceInDays < endDays;
}

export const formatEmployerIdentificationNumber = (value) => {
  let result;

  if (value === null || value === undefined) {
    return '';
  }

  const rawEIN = parseNumber(value?.toString());
  const rawLength = rawEIN.length;

  if (rawLength >= 9) {
    result = rawEIN.substring(0, 9);
  }

  if (rawLength <= 2) {
    result = rawEIN;
  }

  if (rawLength > 2) {
    result = rawEIN.substring(0, 2) + '-' + rawEIN.substring(2, rawLength);
  }

  return result;
};

export async function formatApprovalPdf(pdfInstance) {
  let boltApprovalPdfBinary = '';
  const boltApprovalPdfArrayBuffer = await pdfInstance?.blob?.arrayBuffer();
  const boltApprovalPdfBytes = new Uint8Array(boltApprovalPdfArrayBuffer);
  for (const byte of boltApprovalPdfBytes) {
    boltApprovalPdfBinary += String.fromCharCode(byte);
  }

  return btoa(boltApprovalPdfBinary);
}

//check if object is undefined/null/empty or whitespaces
export const isEmptyOrSpaces = (string: string) => !string?.trim();

export const formatDateWithTime = (datetime) => {
  return new Date(datetime).toLocaleString();
};

// Formats a string to title case
export const toTitleCase = (str: string) => {
  return str.replaceAll(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
  });
};

export function parseTime(timeStr: string): { hours: number; minutes: number } {
  const [time, modifier] = timeStr.split(' ');
  const [hours, minutes] = time.split(':').map(Number);

  if (modifier === 'pm' && hours !== 12) {
    return { hours: hours + 12, minutes };
  } else if (modifier === 'am' && hours === 12) {
    return { hours: 0, minutes };
  }

  return { hours, minutes };
}

export const hexToRgba = (hex: string, opacity: number) => {
  if (!hex || typeof hex !== 'string') {
    logError('Invalid hex color:', hex);
    return 'rgba(0, 0, 0, 0)'; // Return a default value or handle the error as needed
  }
  const bigint = Number.parseInt(hex.replace('#', ''), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};
